import { ReactNode, useMemo } from "react";
import { SurveyStatus } from "shared/types";
import { Check, InfoCircle, XClose, Loading02 } from "untitledui-js-base";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  TimeHalfPassIcon,
  MinusSignIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { Verification } from "views/applications-id/components/ApplicantsTable/types";

enum AggregateStatus {
  NONE = "NONE",
  COMPLETED = "COMPLETED",
  PARTIALLY_COMPLETED = "PARTIALLY_COMPLETED",
  PENDING = "PENDING",
  NO_RESPONSE = "NO_RESPONSE",
  ISSUE_WITH_SURVEY = "ISSUE_WITH_SURVEY"
}

interface LandLordVerificationStatusBadgeProps {
  verification?: Verification;
}

export const LandLordVerificationStatusBadge = ({
  verification
}: LandLordVerificationStatusBadgeProps) => {
  const surveyStatus = useMemo(() => {
    if (!verification) return undefined;

    try {
      const firstStep = verification.steps[0];
      const addressData = firstStep.data?.address;

      if (!addressData || addressData.length === 0) return AggregateStatus.NONE;

      const rentAddresses = addressData?.filter(
        (addr) => addr.housing_type === "Rent" && addr.rent.landlord_email
      );

      const allSurveyStatuses = rentAddresses
        .map((addr) => {
          const surveys =
            verification.landlord_has_sent_surveys[addr.rent.landlord_email];
          return surveys[0]?.status;
        })
        .filter(Boolean);

      if (allSurveyStatuses.length === 0) return undefined;

      const finishedCount = allSurveyStatuses.filter(
        (status) => status === SurveyStatus.Completed
      ).length;
      const hasFailedSurvey = allSurveyStatuses.some(
        (status) => status === SurveyStatus.Failed
      );
      const hasNoResponse = allSurveyStatuses.some(
        (status) => status === SurveyStatus.NoResponse
      );
      const hasPendingSurvey = allSurveyStatuses.some(
        (status) =>
          status === SurveyStatus.OriginalSent ||
          status === SurveyStatus.FirstNudgeSent ||
          status === SurveyStatus.SecondNudgeSent ||
          status === SurveyStatus.FinalNudgeSent
      );

      if (hasPendingSurvey) return AggregateStatus.PENDING;

      if (hasFailedSurvey) return AggregateStatus.ISSUE_WITH_SURVEY;

      if (finishedCount > 0 && finishedCount < allSurveyStatuses.length)
        return AggregateStatus.PARTIALLY_COMPLETED;

      if (finishedCount === allSurveyStatuses.length)
        return AggregateStatus.COMPLETED;

      if (hasNoResponse) return AggregateStatus.NO_RESPONSE;

      return AggregateStatus.PENDING;
    } catch {
      return undefined;
    }
  }, [verification]);

  const {
    icon,
    title
  }: {
    icon?: ReactNode;
    title: string;
  } = useMemo(() => {
    if (!surveyStatus)
      return {
        icon: <HugeiconsIcon icon={MinusSignIcon} size="20" />,
        title: "No landlord surveys"
      };

    switch (surveyStatus) {
      case AggregateStatus.COMPLETED:
        return {
          icon: <Check size="20" className="stroke-success-600" />,
          title: "Completed"
        };

      case AggregateStatus.PARTIALLY_COMPLETED:
        return {
          icon: (
            <HugeiconsIcon
              icon={TimeHalfPassIcon}
              size="20"
              className="text-warning-600"
            />
          ),
          title: "Partially completed"
        };

      case AggregateStatus.NO_RESPONSE:
        return {
          icon: <XClose size="20" className="stroke-danger-500" />,
          title: "No response"
        };

      case AggregateStatus.ISSUE_WITH_SURVEY:
        return {
          icon: <InfoCircle size="20" className="stroke-danger-500" />,
          title: "Issue with survey"
        };

      case AggregateStatus.NONE:
        return {
          title: "-"
        };

      case AggregateStatus.PENDING:
      default:
        return {
          icon: <Loading02 size="20" className="stroke-warning-600" />,
          title: "Pending"
        };
    }
  }, [surveyStatus]);

  return (
    <div className="flex items-center gap-2 text-content-1000">
      {icon && <span>{icon}</span>}
      <span>{title}</span>
    </div>
  );
};
